const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const formatMessage = require('format-message');
const SensorPeripheral = require('../common/sensor-peripheral');
const { default: bleUtils } = require('../../util/ble');
const { BLE_CONFIG } = require('../../util/bleCharac');

/**
 * 三色灯模块
 * 
 * @readonly
 */
const DEVICE_OPT = {
    type: 'sensor'
};

/**
 * Manage communication with a YM-RYG peripheral over a Scratch Link client socket.
 */
class YMRYGPeripheral extends SensorPeripheral {
    constructor(runtime, deviceId, originalDeviceId) {
        super(runtime, deviceId, originalDeviceId, null, BLE_CONFIG, DEVICE_OPT);
    }
}

const LIGHT_MAP = {
    GREEN: 0x04,
    YELLOW: 0x02,
    RED: 0x01,
    RED_GREEN: 0x05,
    RED_YELLOW: 0x03,
    YELLOW_GREEN: 0x06,
    ALL_ON: 0x07,
    ALL_OFF: 0x00
};

class ScratchYMRYGDevice {
    get DEVICE_ID() {
        return 'ymRYG';
    }

    constructor(runtime, originalDeviceId) {
        this.runtime = runtime;
        this._peripheral = new YMRYGPeripheral(this.runtime, this.DEVICE_ID, originalDeviceId);
        this.bleUtils = new bleUtils();
    }

    getInfo() {
        return [{
            id: 'ymRYG',
            name: formatMessage({
                id: 'YMRYG.categoryName',
                default: 'YM RYG',
                description: 'Label for the YM RYG extension category'
            }),
            color1: "#FF9800",
            color2: "#FBC02D",
            color3: "#388E3C",
            blocks: [
                {
                    opcode: 'connect',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMRYG.ble.connect',
                        default: 'connect to YM-RYG-[DEVICE_NAME]',
                        description: 'Connect to YM RYG'
                    }),
                    arguments: {
                        DEVICE_NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: ' ',
                        }
                    }
                },
                {
                    opcode: 'disConnect',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMRYG.ble.disconnect',
                        default: 'disconnect from YM RYG',
                        description: 'Disconnect from YM RYG'
                    })
                },
                {
                    opcode: 'setLight',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMRYG.setLight',
                        default: 'set light [LIGHT]',
                        description: 'Set RYG light'
                    }),
                    arguments: {
                        LIGHT: {
                            type: ArgumentType.STRING,
                            menu: 'lightMenu',
                            defaultValue: 'GREEN'
                        }
                    }
                },
                {
                    opcode: 'setRLightSwitch',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMRYG.setRLightSwitch',
                        default: 'turn red light [SWITCH]',
                        description: 'Turn the light on or off'
                    }),
                    arguments: {
                        SWITCH: {
                            type: ArgumentType.NUMBER,
                            menu: 'lightSwitch'
                        }
                    }
                },
                {
                    opcode: 'setYLightSwitch',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMRYG.setYLightSwitch',
                        default: 'turn yellow light [SWITCH]',
                        description: 'Turn the light on or off'
                    }),
                    arguments: {
                        SWITCH: {
                            type: ArgumentType.NUMBER,
                            menu: 'lightSwitch'
                        }
                    }
                },
                {
                    opcode: 'setGLightSwitch',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMRYG.setGLightSwitch',
                        default: 'turn green light [SWITCH]',
                        description: 'Turn the light on or off'
                    }),
                    arguments: {
                        SWITCH: {
                            type: ArgumentType.NUMBER,
                            menu: 'lightSwitch'
                        }
                    }
                },
            ],
            menus: {
                lightMenu: {
                    acceptReporters: false,
                    items: [
                        { text: formatMessage({ id: 'YMRYG.light.green', default: 'green', description: '' }), value: 'GREEN' },
                        { text: formatMessage({ id: 'YMRYG.light.yellow', default: 'yellow', description: '' }), value: 'YELLOW' },
                        { text: formatMessage({ id: 'YMRYG.light.red', default: 'red', description: '' }), value: 'RED' },
                        { text: formatMessage({ id: 'YMRYG.light.red_green', default: 'red & green', description: '' }), value: 'RED_GREEN' },
                        { text: formatMessage({ id: 'YMRYG.light.red_yellow', default: 'red & yellow', description: '' }), value: 'RED_YELLOW' },
                        { text: formatMessage({ id: 'YMRYG.light.yellow_green', default: 'yellow & green', description: '' }), value: 'YELLOW_GREEN' },
                        { text: formatMessage({ id: 'YMRYG.light.all_on', default: 'all on', description: '' }), value: 'ALL_ON' },
                        { text: formatMessage({ id: 'YMRYG.light.all_off', default: 'all off', description: '' }), value: 'ALL_OFF' }
                    ]
                },
                lightSwitch: {
                   
                    items: [
                        {
                            text: formatMessage({
                                id: 'YMLED.setLightSwitch.1',
                                default: 'On',
                                description: 'Light switch on'
                            }), value: '1'
                        },
                        {
                            text: formatMessage({
                                id: 'YMLED.setLightSwitch.0',
                                default: 'Off',
                                description: 'Light switch off'
                            }), value: '0'
                        }
                    ]
                },
            }
        }];
    }

    async connect(args) {
        const deviceName = 'YM-RYG-' + (args.DEVICE_NAME?.trim() || "");
        const connected = await this.isConnected();
        if (connected) {
            console.log('Already connected to device');
            return;
        }
        console.log(`Connecting to YM RYG device: ${deviceName}`);
        try {
            await this.bleUtils.connect(deviceName, BLE_CONFIG.SERVICE_UUID);
            console.log(`Connected to device: ${deviceName}`);
        } catch (error) {
            console.error(`Failed to connect to ${deviceName}:`, error);
        }
    }

    async isConnected() {
        return this.bleUtils.device !== null && this.bleUtils.device.gatt.connected;
    }

    async disConnect() {
        if (this.bleUtils.device) {
            try {
                await this.bleUtils.disConnect(this.bleUtils.device);
                console.log('Disconnected from device');
            } catch (error) {
                console.error('Failed to disconnect:', error);
            }
        }
    }
    async setRLightSwitch(args) {
        const state = args.SWITCH;
        let value = LIGHT_MAP.RED;
        console.log(`Turning light ${state === '1' ? 'on' : 'off'}`);
        if (this.bleUtils.device) {
            try {
                //
                const characteristic = await this.bleUtils.getCharacteristic(this.bleUtils.service, BLE_CONFIG.threeLights);
                const stateValue = parseInt(state, 10);
                if (isNaN(stateValue)) {
                    console.error('Invalid state value:', state);
                    return;
                }
                if(stateValue ==0){
                    value = LIGHT_MAP.ALL_OFF;
                }
               
                const dataView = await this.bleUtils.readCharacteristic(characteristic);
                let currentValue = dataView.getUint8(0); // 读取当前状态字节
    
                // 修改红灯位（第0位）
                // 先清0，再设置
                currentValue = (currentValue & 0xFE) | (stateValue ? 0x01 : 0x00);
                //         ↑ mask: 保留高7位，清掉第0位
                //                    ↑ 设置第0位为 1 或 0
        
                const stateArray = new Uint8Array([currentValue]);
             
                await this.bleUtils.writeCharacteristic(characteristic, stateArray);
            } catch (error) {
                console.error('Failed to set light switch:', error);
            }
        }
    }


    

    async setYLightSwitch(args) {
        const state = args.SWITCH;
        let value = LIGHT_MAP.YELLOW;
        console.log(`Turning light ${state === '1' ? 'on' : 'off'}`);
        if (this.bleUtils.device) {
            try {
                const characteristic = await this.bleUtils.getCharacteristic(this.bleUtils.service, BLE_CONFIG.threeLights);
                const stateValue = parseInt(state, 10);
                if (isNaN(stateValue)) {
                    console.error('Invalid state value:', state);
                    return;
                }
                if(stateValue ==0){
                    value = LIGHT_MAP.ALL_OFF;
                }
                const dataView = await this.bleUtils.readCharacteristic(characteristic);
                let currentValue = dataView.getUint8(0);

                      // 修改黄灯位（第1位）
                     currentValue = (currentValue & 0xFD) | (stateValue ? 0x02 : 0x00);
                     //         ↑ mask: 0xFD = 11111101 (清第1位)

                     const stateArray = new Uint8Array([currentValue]);
              
               
                await this.bleUtils.writeCharacteristic(characteristic, stateArray);
            } catch (error) {
                console.error('Failed to set light switch:', error);
            }
        }
    }
    async setGLightSwitch(args) {
        const state = args.SWITCH;
        let value = LIGHT_MAP.GREEN;
        console.log(`Turning light ${state === '1' ? 'on' : 'off'}`);
        if (this.bleUtils.device) {
            try {
                const characteristic = await this.bleUtils.getCharacteristic(this.bleUtils.service, BLE_CONFIG.threeLights);
                const stateValue = parseInt(state, 10);
                if (isNaN(stateValue)) {
                    console.error('Invalid state value:', state);
                    return;
                }
                if(stateValue ==0){
                    value = LIGHT_MAP.ALL_OFF;
                }
                const dataView = await this.bleUtils.readCharacteristic(characteristic);
                let currentValue = dataView.getUint8(0);
        
                // 修改绿灯位（第2位）
                currentValue = (currentValue & 0xFB) | (stateValue ? 0x04 : 0x00);
                //         ↑ mask: 0xFB = 11111011 (清第2位)
        
                const stateArray = new Uint8Array([currentValue]);
             
               
                await this.bleUtils.writeCharacteristic(characteristic, stateArray);
            } catch (error) {
                console.error('Failed to set light switch:', error);
            }
        }
    }
    async setLight(args) {
        const light = args.LIGHT || 'GREEN';
        const value = LIGHT_MAP[light] !== undefined ? LIGHT_MAP[light] : LIGHT_MAP.GREEN;
        if (this.bleUtils.device) {
            try {
                const characteristic = await this.bleUtils.getCharacteristic(this.bleUtils.service, BLE_CONFIG.threeLights);
                const data = new Uint8Array([value]);
                await this.bleUtils.writeCharacteristic(characteristic, data);
                console.log('Set light to', light, 'value', value);
            } catch (error) {
                console.error('Failed to set light:', error);
            }
        }
    }
}

module.exports = ScratchYMRYGDevice;
